import { HackingIndicatorBehavior } from '@phaser/behaviors/HackingIndicator.behavior';
import { ActiveCritBehavior } from '@phaser/behaviors/ActiveCrit.behavior';
import { HealthbarBehavior } from '@phaser/behaviors/Healthbar.behavior';
import { ShieldbarBehavior } from '@phaser/behaviors/Shieldbar.behavior';
import { CARD_HEIGHT, CARD_WIDTH, Game } from '@phaser/scenes/Game';
import { EffectBehavior } from '@phaser/behaviors/Effect.behavior';
import { AttackBehavior } from '@phaser/behaviors/Attack.behavior';
import { ItemDropObject } from '@phaser/objects/ItemDrop.object';
import { CardBehavior } from '@phaser/behaviors/Card.behavior';
import { deductShieldAndHealth } from '@phaser/helpers/Math';
import { playHit } from '@components/sound/Sound';
import { DEPTH } from '@phaser/data/depth';
import { timeDiff } from '@api/websocket';
import * as Shared from '@shared/lib';
import i18next from 'i18next';

export abstract class CardObject {
  public container: Phaser.GameObjects.Container;
  public data: Shared.CardSpawnMessage;
  public pos: number;
  public id: string;

  public get hitbox() {
    return {
      y: this.container.y - CARD_HEIGHT / 2,
      x: this.container.x - CARD_WIDTH / 2,
      height: CARD_HEIGHT,
      width: CARD_WIDTH
    };
  }

  public get tweenTargets() {
    return [this.container, this._cardBehavior.maskRect];
  }

  public get healthbar() {
    return this._healthbarBehavior.healthbar;
  }

  protected _hackingIndicatorBehavior: HackingIndicatorBehavior;
  protected _activeCritBehavior: ActiveCritBehavior;
  protected _healthbarBehavior: HealthbarBehavior;
  protected _shieldbarBehavior: ShieldbarBehavior;
  protected _attackBehavior: AttackBehavior;
  protected _effectBehavior: EffectBehavior;
  protected _cardBehavior: CardBehavior;
  protected _scene: Game = Game.current;
  protected _deathCheck: Timer;
  protected _health: number;
  protected _shield: number;

  constructor(data: Shared.CardSpawnMessage) {
    this.data = data;
    this.id = data.core.id;
    this.pos = data.core.pos;

    this.container = this._scene.add.container(this._scene.cameras.main.width / 2, 0)
      .setDepth(DEPTH.CARD);
    const rectangle = this._scene.add.rectangle(0, 0, CARD_WIDTH, CARD_HEIGHT, 0xFFFFFF)
      .setOrigin(0.5);
    this.container.add(rectangle);

    this._cardBehavior = new CardBehavior(this);
    this._effectBehavior = new EffectBehavior(this);
    this._attackBehavior = new AttackBehavior(this);
    this._healthbarBehavior = new HealthbarBehavior(this);
    this._shieldbarBehavior = new ShieldbarBehavior(this);
    this._activeCritBehavior = new ActiveCritBehavior(this);
    this._hackingIndicatorBehavior = new HackingIndicatorBehavior(this);

    this.data.core.effects
      .filter(e => e.duration - timeDiff() > 750)
      .forEach(e => this._effectBehavior.addEffect(e.effectCode, e.duration - timeDiff()));

    this._health = data.core.hp;
    this._shield = data.core.shield;
    this._cardBehavior.spawnAnimation();

    this._deathCheck = setInterval(() => {
      if (this._scene.deadIds.includes(this.id)) {
        this.die([]);
      }
    }, 250);
  }

  public onActiveCrit() {
    this._activeCritBehavior.setActiveCrit();
  }

  public die(lootDrops: Shared.LootDropMessage[] = []) {
    clearInterval(this._deathCheck);

    const { x, y } = this.container;

    lootDrops
      .forEach((lootDrop) => new ItemDropObject(x, y, lootDrop));

    if (this.data.enemy) playHit();

    this._cardBehavior.killAnimation();
  }

  public damage(dmg: number) {
    this._activeCritBehavior.destroy();
    this._cardBehavior.damageAnimation();

    const { health, shield } = deductShieldAndHealth(this._shield, this._health, dmg);
    this._shield = shield;
    this._health = health;

    this._shieldbarBehavior.shield = this._shield;
    this._healthbarBehavior.health = this._health;
  }

  public showHackingIndicator() {
    this._hackingIndicatorBehavior.show();
  }

  public sync(data: Shared.CardSyncMessage) {
    this._health = data.health;
    this._shield = data.shield;

    if (
      data.damageInterval
      && this.data.enemy
      && this.data.enemy.attackInterval !== data.damageInterval
    ) {
      this.data.enemy.attackInterval = data.damageInterval;
      this._attackBehavior.updateInterval();
    }

    this._healthbarBehavior.max = data.maxHealth;
    this._healthbarBehavior.health = data.health;

    this._shieldbarBehavior.max = data.maxShield;
    this._shieldbarBehavior.shield = data.shield;
  }

  public onAbility(abilityCode: number) {
    const key = Shared.abilityData.find(a => a.id === abilityCode)?.key;
    if (!key) return;

    const text = i18next.t(`Game.Abilities.Text.${key}`);
    this._cardBehavior.abilityAnimation(text);
  }

  public onEffect(effectCode: number, duration: number) {
    this._effectBehavior.addEffect(effectCode, duration);
  }

  public abstract destroy(): void;
}